import {
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  provideSoftAppsModule,
  provideSearchModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import { SOFTLINE_CONFIG_ARTIKEL_INFO_MODAL_COMPONENT } from '@softapps/wws/core';
import {
  ApplicationStore,
  getModuleGroupByName,
  SOFTLINE_CONFIG_OFFLINE_FEATURE,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import {
  ConnectionHttpService,
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { default as de } from '../i18n/de.json';
import { default as deTitles } from '../i18n/de.titles.json';
import {
  SOFTLINE_API_ITEM_FAVORITE_ITEMS,
  SOFTLINE_API_ITEM_FAVORITE_LISTS,
  SOFTLINE_API_ITEM_PRICE,
  SOFTLINE_API_ITEM_STOCK,
} from './artikel-info.api';
import {
  SOFTLINE_CONFIG_ARTIKEL_INFO_DETAIL_SEQUENCE_SIZE,
  SOFTLINE_FEATURE_ARTIKEL_INFO_FAVORITE_ITEMS,
  SOFTLINE_FEATURE_ARTIKEL_INFO_FAVORITE_LISTS,
  SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM,
  SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM_DETAIL,
  SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM_PRICE,
  SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM_PRICELIST,
  SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM_STOCK,
  SOFTLINE_FEATURE_ARTIKEL_INFO_SCAN_LISTE,
  SOFTLINE_FEATURE_ARTIKEL_INFO_UMSATZABFRAGE,
  SOFTLINE_FEATURE_LOCAL_SEARCH_HISTORY,
} from './artikel-info.shared';
import { artikelInfoRoutes } from './artikel-info.routes';
import { provideArtikelInfoGlobals } from './artikel-info.globals';
import { ItemSearchResult } from './components/item-search-result/item-search-result.component';
import { ArtikelDetailComponent } from './pages/artikel-detail/artikel-detail.component';
import { FavoriteResourceService } from './services/favorite-resource.service';
import { FavoriteService } from './services/favorite.service';
import { GesamtabfrageBewegungenService } from './services/gesamtabfrage-bewegungen.service';
import { ItemService } from './services/item.service';
import { UmsatzabfrageService } from './services/umsatzabfrage.service';
import { FavoriteItemsStore } from './store/favorite-items.store';
import { FavoriteListsStore } from './store/favorite-lists.store';
import { ItemDetailStore } from './store/item-detail.store';
import { ItemPriceStore } from './store/item-price.store';
import { ItemDetailPricelistStore } from './store/item-pricelist.store';
import { ItemStockStore } from './store/item-stock.store';
import { ItemStore } from './store/item.store';
import { ScanListStore } from './store/scan-liste.store';
import { SearchHistoryStore } from './store/search-history.store';
import { UmsatzabfrageStore } from './store/umsatzabfrage.store';
import { Item } from './types/item';
import { ItemQuery } from './types/item-query';

export const artikelInfoProviders = [
  provideEnvironmentInitializer(async () => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'artikel-info',
      language: 'de',
      translations: de,
    });

    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: 'Artikel-Info',
      icon: 'fa-regular fa-square-info',
      route: '/artikel-info',
      permission: 'ARTIKELINFO',
      group: getModuleGroupByName('Stammdaten'),
    });
  }),
  {
    provide: SOFTLINE_CONFIG_ARTIKEL_INFO_DETAIL_SEQUENCE_SIZE,
    useValue: 4,
  },
  FavoriteService,
  GesamtabfrageBewegungenService,
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM,
      feature: ItemStore.feature,
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_LOCAL_SEARCH_HISTORY,
      feature: SearchHistoryStore.feature,
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_SCAN_LISTE,
      feature: ScanListStore.feature,
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM_PRICE,
      feature: ItemPriceStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (o: any) => new SoftlineEntityService(o, SOFTLINE_API_ITEM_PRICE),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_FAVORITE_LISTS,
      feature: FavoriteListsStore.feature,
      providers: [
        {
          provide: FavoriteResourceService,
          useClass: FavoriteResourceService,
        },
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (o: any) =>
            new SoftlineEntityService(o, SOFTLINE_API_ITEM_FAVORITE_LISTS),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_FAVORITE_ITEMS,
      feature: FavoriteItemsStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (o: any) =>
            new SoftlineEntityService(o, SOFTLINE_API_ITEM_FAVORITE_ITEMS),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM_DETAIL,
      feature: ItemDetailStore.feature,
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM_PRICELIST,
      feature: ItemDetailPricelistStore.feature,
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_UMSATZABFRAGE,
      feature: UmsatzabfrageStore.feature,
      providers: [
        {
          provide: UmsatzabfrageService,
          useFactory: () => new UmsatzabfrageService(inject(ConnectionHttpService)),
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_ARTIKEL_INFO_ITEM_STOCK,
      feature: ItemStockStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (o: any) => new SoftlineEntityService(o, SOFTLINE_API_ITEM_STOCK),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    })
  ),
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: { featureName: SOFTLINE_FEATURE_LOCAL_SEARCH_HISTORY, fields: ['history'] },
    multi: true,
  },
  {
    provide: SOFTLINE_CONFIG_ARTIKEL_INFO_MODAL_COMPONENT,
    useValue: ArtikelDetailComponent,
  },

  provideSearchModule<Item>({
    url: '/artikel-info',
    category: 'Artikel',
    icon: 'fa-regular fa-box',
    permission: 'ARTIKELINFO',
    component: ItemSearchResult,
    factory: () => {
      const itemService = inject(ItemService);

      let lastTerm = '';
      let lastResult$: Observable<Item[]> = of([]);

      return {
        results(term: string | null): Observable<Item[]> {
          const normalized = term?.trim() ?? '';

          if (normalized.length < 3) return of([]);
          if (normalized === lastTerm) {
            return lastResult$;
          }

          lastTerm = normalized;
          const query: ItemQuery = { query: normalized } as any;

          lastResult$ = itemService.loadMany(query, 20, 0).pipe(
            map((response) => response.result),
            catchError((err) => {
              return of([]);
            }),
            shareReplay(1)
          );

          return lastResult$;
        },
      };
    },
  }),
];

export function provideArtikelInfoModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'wws/artikel-info',
        title: '#ARTIKELINFO.TITLE',
        icon: 'fa-regular fa-square-info',
        route: '/artikel-info',
        permission: 'ARTIKELINFO',
        group: getModuleGroupByName('Stammdaten'),
      },
      withSoftAppsModuleRoutes({ app: artikelInfoRoutes }),
      withLazySoftAppsModuleTranslation({
        eager: { de: deTitles },
        lazy: { de: () => import('../i18n/de.json') },
      })
    ),
    ...provideArtikelInfoGlobals(),
  ];
}
